import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'

/**
 * Pre-mapping and mapping: which laboratory value lands in which form field.
 *
 * Tenant-scoped (the tenant rides on `X-Tenant-Id` via `authHeaders`) and
 * entirely customer-configurable. **Nothing here names an HL7 field.** A source
 * registers a sample document, a pre-map names a value by whichever of that
 * document's fields identify it, and a mapping binds the name to a form field —
 * so the same screen configures a CSV the middleware converted to JSON.
 */

/**
 * A pre-mapping: a name the tenant gave one flattened path of the example —
 * `obsCode` for `observations[*].code.code`. The Mapping screen offers these as
 * Source and Value fields.
 */
export type LisNamedPath = {
  id: string
  schemaId: string
  name: string
  path: string
}

/**
 * One source, registered by an example message it sends.
 *
 * The example is flattened into paths on the Pre-Mapping tab, and the paths the
 * tenant names come back as `namedPaths`.
 */
export type LisSourceSchema = {
  id: string
  name: string
  description: string | null
  /** One real message from this source, as JSON. */
  sample: unknown | null
  /**
   * Which of the example's fields hold the order number and the test code, as
   * paths. Chosen when the example is set; null only on sources from before
   * that was asked.
   */
  orderIdPath: string | null
  testCodePath: string | null
  active: boolean
  createdBy: string
  namedPaths: LisNamedPath[]
  _count?: { preMaps: number }
}

/** One field that has to hold a particular value for the pre-map to match. */
export type LisPreMapKey = {
  id: string
  path: string
  expectedValue: string
  sortOrder: number
}

/** Where a named value goes. */
export type LisFieldMapping = {
  id: string
  preMapId: string
  formDefinitionId: string
  /**
   * There is no stage here: which order stage this applies to is the target
   * field's own `lisStage`, set in the form builder.
   */
  targetFieldId: string
  /** What happens when this result is sent again — see `listRecordActions`. */
  action: string
  active: boolean
}

/**
 * One thing a mapping can do when a result arrives again.
 *
 * Served by the backend rather than listed here, so adding an action is one
 * entry in `lis-record-action.ts` and it appears in the dropdown with no
 * frontend change — which is the extensibility the design note asked for.
 */
export type LisRecordAction = {
  value: string
  label: string
  description: string
  onRepeat: 'write' | 'keep'
}

/**
 * One value read out of a source's messages: N key fields, and the field
 * carrying the result.
 *
 * The number of keys is deliberately variable — no single field identifies a
 * result on its own, so uniqueness is composed from whichever fields the source
 * happens to make unique.
 *
 * **It has no name.** A value is identified by its content: the same key set and
 * value path saved twice is one value with two destinations, which is what keeps
 * a Pre field and a Post field from both firing.
 */
export type LisPreMap = {
  id: string
  schemaId: string
  description: string | null
  valuePath: string
  active: boolean
  createdBy: string
  keys: LisPreMapKey[]
  mappings: LisFieldMapping[]
  schema?: { id: string; name: string }
}

export type LisMappingFailure = {
  id: string
  ingestId: string | null
  orderId: string | null
  mrn: string | null
  testCode: string
  /**
   * Which value could not be filed, generated from its keys and path. Replaced
   * `observationCode`.
   */
  preMapLabel: string | null
  /** Where it was read from. Replaced `sourceProperty`. */
  valuePath: string | null
  formDefinitionId: string | null
  targetFieldId: string | null
  reason: string
  detail: string
  resolved: boolean
  occurredAt: string
}

/** What a draft pre-map would read, before it is saved. */
export type LisPreMapPreview =
  | { matched: true; value: string | number | boolean; valueType: string }
  | { matched: false; reason: string; detail: string }

export type LisPreMapKeyInput = { path: string; expectedValue: string }

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    headers: authHeaders(init?.body ? { 'Content-Type': 'application/json' } : undefined),
    ...init,
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    let message = text
    try {
      const parsed = JSON.parse(text) as { message?: string | string[] }
      if (parsed.message) message = Array.isArray(parsed.message) ? parsed.message.join(', ') : parsed.message
    } catch {
      /* leave raw text */
    }
    throw new Error(message || `API ${init?.method ?? 'GET'} ${path} failed (${res.status})`)
  }
  const text = await res.text()
  return (text ? JSON.parse(text) : undefined) as T
}

const ROOT = '/integrations/lis/config'

export const lisConfigApi = {
  listSchemas: () => request<LisSourceSchema[]>(`${ROOT}/schemas`),

  createSchema: (body: {
    name: string
    description?: string
    sample: unknown
    orderIdPath: string
    testCodePath: string
  }) => request<LisSourceSchema>(`${ROOT}/schemas`, { method: 'POST', body: JSON.stringify(body) }),

  updateSchema: (
    id: string,
    body: {
      name?: string
      description?: string | null
      /** A replacement example. Names given to its paths are kept. */
      sample?: unknown
      orderIdPath?: string
      testCodePath?: string
      active?: boolean
    },
  ) =>
    request<LisSourceSchema>(`${ROOT}/schemas/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  createNamedPath: (schemaId: string, body: { name: string; path: string }) =>
    request<LisNamedPath>(`${ROOT}/schemas/${schemaId}/named-paths`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateNamedPath: (id: string, body: { name?: string; path?: string }) =>
    request<LisNamedPath>(`${ROOT}/named-paths/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  removeNamedPath: (id: string) =>
    request<{ removed: boolean }>(`${ROOT}/named-paths/${id}`, { method: 'DELETE' }),

  removeSchema: (id: string) =>
    request<{ removed: boolean }>(`${ROOT}/schemas/${id}`, { method: 'DELETE' }),

  listPreMaps: () => request<LisPreMap[]>(`${ROOT}/pre-maps`),

  /**
   * Find-or-create. Sending the same key set and value path again attaches the
   * new destination to the value that already exists rather than making a twin.
   */
  createPreMap: (body: {
    schemaId: string
    description?: string
    valuePath: string
    keys?: LisPreMapKeyInput[]
    destination?: {
      formDefinitionId: string
      targetFieldId: string
      action?: string
    }
  }) => request<LisPreMap>(`${ROOT}/pre-maps`, { method: 'POST', body: JSON.stringify(body) }),

  updatePreMap: (
    id: string,
    body: {
      description?: string | null
      valuePath?: string
      active?: boolean
      keys?: LisPreMapKeyInput[]
    },
  ) => request<LisPreMap>(`${ROOT}/pre-maps/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  removePreMap: (id: string) =>
    request<{ removed: boolean }>(`${ROOT}/pre-maps/${id}`, { method: 'DELETE' }),

  /**
   * Try a draft against the sample without saving it. A path picked off a tree
   * looks right and still resolves to the wrong row, or to two rows.
   */
  previewPreMap: (body: {
    schemaId: string
    valuePath: string
    keys?: LisPreMapKeyInput[]
    document?: unknown
  }) =>
    request<LisPreMapPreview>(`${ROOT}/pre-maps/preview`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  listRecordActions: () =>
    request<{ actions: LisRecordAction[] }>(`${ROOT}/record-actions`).then((r) => r.actions),

  createMapping: (
    preMapId: string,
    body: {
      formDefinitionId: string
      targetFieldId: string
      action?: string
    },
  ) =>
    request<LisFieldMapping>(`${ROOT}/pre-maps/${preMapId}/mappings`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateMapping: (id: string, body: Partial<Omit<LisFieldMapping, 'id' | 'preMapId'>>) =>
    request<LisFieldMapping>(`${ROOT}/mappings/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  removeMapping: (id: string) =>
    request<{ removed: boolean }>(`${ROOT}/mappings/${id}`, { method: 'DELETE' }),

  listFailures: (includeResolved = false) =>
    request<LisMappingFailure[]>(
      `${ROOT}/failures${includeResolved ? '?includeResolved=true' : ''}`,
    ),

  resolveFailure: (id: string) =>
    request<LisMappingFailure>(`${ROOT}/failures/${id}/resolve`, { method: 'POST' }),
}
